import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import { PlusIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import { useFinancialAccounts } from '../../../hooks/queries/useFinancialAccounts';
import { PATHS } from '../../../paths';
import { useNavigateWithDateRange } from '../../../providers/DateRangeProvider';
import { isSupportedFinancialAccount } from '../../../utils/account/accounts';
import { ACCOUNT_SECTIONS, groupAccountsBySection } from '../../../utils/account/accountSections';
import AccountGroup from '../../AccountGroup';
import PlaidLinkButton from '../../plaid/PlaidLinkButton';
import Widget from '../../Widget';

const INCLUDE_INVESTMENTS = false;

/**
 * Right-column widget on the dashboard showing the user's financial accounts
 * grouped by section (checking, savings, credit cards). Includes a "connect"
 * button via Plaid Link and navigates to the account detail pane on click.
 * Sync status is per account row; the header carries none.
 */
const DashboardAccountsWidget = () => {
  const navigate = useNavigateWithDateRange();
  const { financialAccounts, loading } = useFinancialAccounts();

  const filteredAccounts = React.useMemo(
    () =>
      financialAccounts?.filter((account) =>
        isSupportedFinancialAccount(account, { includeInvestments: INCLUDE_INVESTMENTS })
      ) ?? [],
    [financialAccounts]
  );

  const groupedAccounts = React.useMemo(
    () => groupAccountsBySection(filteredAccounts, { includeInvestments: INCLUDE_INVESTMENTS }),
    [filteredAccounts]
  );

  const handleAccountClick = (accountId: string) => navigate(PATHS.ACCOUNT(accountId));

  const addButton = <PlaidLinkButton color="tertiary" size="sm" iconLeading={<PlusIcon className="size-5" />} />;

  if (loading) {
    return (
      <Widget>
        <Widget.Header title="Accounts" />
        <Widget.Body>
          <LoadingIndicator className="h-[200px]" />
        </Widget.Body>
      </Widget>
    );
  }

  if (filteredAccounts.length === 0) {
    return null;
  }

  const nonEmptySections = ACCOUNT_SECTIONS.filter((section) => groupedAccounts[section].length > 0);

  return (
    <Widget>
      <Widget.Header title="Accounts" trailing={addButton} />
      <Widget.Body className="bg-primary">
        {nonEmptySections.map((section, index) => (
          <AccountGroup
            key={section}
            section={section}
            accounts={groupedAccounts[section]}
            onAccountClick={handleAccountClick}
            compact
            flat
            showTopBorder={index > 0}
          />
        ))}
      </Widget.Body>
    </Widget>
  );
};

export default DashboardAccountsWidget;
